import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Typography,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  ListItemButton,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PersonIcon from '@mui/icons-material/Person';
import SpeedIcon from '@mui/icons-material/Speed';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { format } from 'date-fns';
import type { Inspection, InspectionStatus, InspectionRecommendation } from '@/types';
import type { WorkOrderItem } from '@/hooks/useWorkOrders';
import type { InspectionOfferFinding } from '@/hooks/useInspectionOffers';
import { UserAvatar, RegistrationPlate } from '@/components/common';
import { LinkToWorkOrderDialog } from './LinkToWorkOrderDialog';
import { dateFnsLocale, formatNumber } from '@/utils/locale';

interface InspectionSummaryProps {
  inspection: Inspection;
  workOrderNumber?: string;
  workOrderItems?: WorkOrderItem[];
  offerFindings?: InspectionOfferFinding[];
  onLinkRecommendation?: (recId: string, workOrderItemId: string) => Promise<void>;
  onCreateAndLinkRecommendation?: (recId: string, recommendation: {
    description: string;
    partsCostCents: number;
    laborCostCents: number;
    laborHours?: number;
  }) => Promise<void>;
  onUnlinkRecommendation?: (recId: string) => Promise<void>;
  onAddToOffer?: (recommendationIds: string[]) => Promise<void>;
  onEditRecommendation?: (recId: string) => void;
  onDeleteRecommendation?: (recId: string) => void;
  managedFindingIds?: string[];
  isLinking?: boolean;
  isAddingToOffer?: boolean;
}

export function InspectionSummary({
  inspection,
  workOrderNumber,
  workOrderItems = [],
  offerFindings,
  onLinkRecommendation,
  onCreateAndLinkRecommendation,
  onUnlinkRecommendation,
  onAddToOffer,
  onEditRecommendation,
  onDeleteRecommendation,
  managedFindingIds = [],
  isLinking = false,
  isAddingToOffer = false,
}: InspectionSummaryProps) {
  const { t } = useTranslation('inspection');
  const theme = useTheme();
  const [selectedRecommendation, setSelectedRecommendation] = useState<InspectionRecommendation | null>(null);
  const [selectedOfferIds, setSelectedOfferIds] = useState<string[]>([]);

  const statusConfig: Record<InspectionStatus, { label: string; color: 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info' }> = {
    draft: { label: t('summary.draft'), color: 'default' },
    pending: { label: t('status.pending'), color: 'default' },
    in_progress: { label: t('summary.inProgress'), color: 'warning' },
    completed: { label: t('summary.completed'), color: 'success' },
    sent_to_customer: { label: t('summary.sent'), color: 'info' },
    approved: { label: t('summary.approved'), color: 'success' },
    rejected: { label: t('summary.rejected'), color: 'error' },
  };

  const urgencyConfig = {
    immediate: { label: t('summary.urgent'), color: theme.palette.error.main },
    soon: { label: t('summary.soon'), color: theme.palette.warning.main },
    future: { label: t('summary.noRush'), color: theme.palette.success.main },
  };
  const [dialogOpen, setDialogOpen] = useState(false);

  // Calculate statistics
  const allItems = inspection.categories.flatMap((cat) => cat.items);
  const totalItems = allItems.length;
  const inspectedItems = allItems.filter((item) => item.condition !== null).length;
  const goodItems = allItems.filter((item) => item.condition === 'good').length;
  const fairItems = allItems.filter((item) => item.condition === 'fair').length;
  const poorItems = allItems.filter((item) => item.condition === 'poor').length;
  const criticalItems = allItems.filter((item) => item.condition === 'critical').length;

  const itemRecommendations = allItems
    .map((item) => item.recommendation)
    .filter((rec): rec is InspectionRecommendation => rec !== undefined);

  const findings: InspectionOfferFinding[] = offerFindings ?? itemRecommendations.map((rec) => ({
    id: rec.id,
    description: rec.description,
    urgency: rec.urgency,
    estimatedPriceCents: rec.estimatedPriceCents,
    workOrderItemId: rec.workOrderItemId ?? null,
    offerId: null,
  }));

  const immediateCount = findings.filter((r) => r.urgency === 'immediate').length;
  const soonCount = findings.filter((r) => r.urgency === 'soon').length;

  const formatPrice = (cents: number) => `€${(cents / 100).toFixed(2)}`;

  const canLink = onLinkRecommendation && onCreateAndLinkRecommendation && onUnlinkRecommendation;

  const handleRecommendationClick = (rec: InspectionOfferFinding) => {
    if (!canLink) return;
    setSelectedRecommendation({
      id: rec.id,
      description: rec.description,
      urgency: rec.urgency,
      estimatedPriceCents: rec.estimatedPriceCents,
      workOrderItemId: rec.workOrderItemId,
    });
    setDialogOpen(true);
  };

  const toggleOfferSelection = (recId: string, enabled: boolean) => {
    if (!enabled) return;
    setSelectedOfferIds((current) =>
      current.includes(recId) ? current.filter((id) => id !== recId) : [...current, recId],
    );
  };

  const handleAddToOffer = async () => {
    if (!onAddToOffer || selectedOfferIds.length === 0) return;
    try {
      await onAddToOffer(selectedOfferIds);
      setSelectedOfferIds([]);
    } catch {
      // The caller displays the error; keep the selection available for retry.
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedRecommendation(null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Status Card */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">{t('summary.status')}</Typography>
            <Chip
              label={statusConfig[inspection.status].label}
              color={statusConfig[inspection.status].color}
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {t('summary.created')} {format(new Date(inspection.createdAt), 'd.M.yyyy HH:mm', { locale: dateFnsLocale() })}
          </Typography>
          {inspection.completedAt && (
            <Typography variant="body2" color="text.secondary">
              {t('summary.completedAt')} {format(new Date(inspection.completedAt), 'd.M.yyyy HH:mm', { locale: dateFnsLocale() })}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Vehicle & Customer */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('summary.vehicleAndCustomer')}
          </Typography>
          <List dense disablePadding>
            <ListItem disablePadding sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <DirectionsCarIcon fontSize="small" color="action" />
              </ListItemIcon>
              <ListItemText
                primary={
                  <RegistrationPlate registrationNumber={inspection.vehicle.registrationNumber ?? '-'} />
                }
                secondary={`${inspection.vehicle.make} ${inspection.vehicle.model} (${String(inspection.vehicle.year)})`}
              />
            </ListItem>
            <ListItem disablePadding sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <SpeedIcon fontSize="small" color="action" />
              </ListItemIcon>
              <ListItemText primary={`${formatNumber(inspection.mileage)} km`} />
            </ListItem>
            <ListItem disablePadding sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <PersonIcon fontSize="small" color="action" />
              </ListItemIcon>
              <ListItemText
                primary={`${inspection.customer.firstName} ${inspection.customer.lastName}`}
                secondary={inspection.customer.phone}
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Inspection Progress */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('summary.inspectionStatus')}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('summary.inspected')}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {inspectedItems} / {totalItems}
            </Typography>
          </Box>

          {/* Condition Breakdown */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
              <Typography variant="body2" sx={{ flex: 1 }}>
                {t('summary.good')}
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {goodItems}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningIcon sx={{ color: 'warning.main', fontSize: 18 }} />
              <Typography variant="body2" sx={{ flex: 1 }}>
                {t('summary.fair')}
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {fairItems}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningIcon sx={{ color: 'warning.dark', fontSize: 18 }} />
              <Typography variant="body2" sx={{ flex: 1 }}>
                {t('summary.poor')}
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {poorItems}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ErrorIcon sx={{ color: 'error.main', fontSize: 18 }} />
              <Typography variant="body2" sx={{ flex: 1 }}>
                {t('summary.critical')}
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {criticalItems}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Recommendations */}
      {findings.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('summary.recommendations')}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              {immediateCount > 0 && (
                <Chip
                  size="small"
                  color="error"
                  label={t('summary.immediateCount', { count: immediateCount })}
                />
              )}
              {soonCount > 0 && (
                <Chip
                  size="small"
                  color="warning"
                  label={t('summary.soonCount', { count: soonCount })}
                />
              )}
            </Box>

            {/* Recommendation List */}
            <List dense disablePadding sx={{ mb: 2 }}>
              {findings.map((rec) => {
                const isLinked = !!rec.workOrderItemId;
                const onOffer = !!rec.offerId;
                const managed = onOffer || managedFindingIds.includes(rec.id);
                const itemSx = {
                  borderRadius: 1,
                  mb: 0.5,
                  border: 1,
                  borderColor: onOffer ? 'info.light' : isLinked ? 'success.light' : 'divider',
                  backgroundColor: onOffer ? 'info.50' : isLinked ? 'success.50' : 'transparent',
                };

                const itemContent = (
                  <>
                    {onAddToOffer && (
                      <Checkbox
                        size="small"
                        checked={selectedOfferIds.includes(rec.id)}
                        disabled={managed}
                        onClick={(e) => { e.stopPropagation(); }}
                        onChange={() => { toggleOfferSelection(rec.id, !managed); }}
                        inputProps={{
                          'aria-label': t('offer.selectFinding', { description: rec.description }),
                        }}
                        sx={{ mr: 0.5 }}
                      />
                    )}
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {onOffer ? (
                        <Tooltip title={t('offer.alreadyOnOffer')}>
                          <LinkIcon sx={{ fontSize: 18, color: 'info.main' }} />
                        </Tooltip>
                      ) : isLinked ? (
                        <Tooltip title={t('summary.linkedToWorkOrder')}>
                          <LinkIcon sx={{ fontSize: 18, color: 'success.main' }} />
                        </Tooltip>
                      ) : (
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            backgroundColor: urgencyConfig[rec.urgency].color,
                          }}
                        />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      disableTypography
                      primary={
                        <Typography variant="body2" fontWeight={500} noWrap>
                          {rec.description}
                        </Typography>
                      }
                      secondary={
                        <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Chip
                            size="small"
                            label={urgencyConfig[rec.urgency].label}
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              backgroundColor: alpha(urgencyConfig[rec.urgency].color, 0.08),
                              color: urgencyConfig[rec.urgency].color,
                            }}
                          />
                          <Typography variant="caption" fontWeight={600}>
                            {formatPrice(rec.estimatedPriceCents)}
                          </Typography>
                          {onOffer && (
                            <Chip size="small" color="info" label={t('offer.alreadyOnOffer')} />
                          )}
                        </Box>
                      }
                    />
                    {onEditRecommendation && !managed && (
                      <Tooltip title={t('recommendation.edit')}>
                        <IconButton size="small" aria-label={t('recommendation.edit')} onClick={(event) => {
                          event.stopPropagation(); onEditRecommendation(rec.id);
                        }}><EditIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                    {onDeleteRecommendation && !managed && (
                      <Tooltip title={t('recommendation.delete')}>
                        <IconButton size="small" aria-label={t('recommendation.delete')} onClick={(event) => {
                          event.stopPropagation(); onDeleteRecommendation(rec.id);
                        }}><DeleteIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                    {canLink && !managed && (
                      <Tooltip title={isLinked ? t('unlinkFromWorkOrder') : t('linkToWorkOrder')}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRecommendationClick(rec);
                          }}
                          aria-label={isLinked ? t('unlinkFromWorkOrder') : t('linkToWorkOrder')}
                        >
                          {isLinked ? (
                            <LinkOffIcon sx={{ fontSize: 18 }} />
                          ) : (
                            <LinkIcon sx={{ fontSize: 18 }} />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}
                  </>
                );

                return canLink && !managed ? (
                  <ListItemButton
                    key={rec.id}
                    onClick={() => { handleRecommendationClick(rec); }}
                    sx={itemSx}
                  >
                    {itemContent}
                  </ListItemButton>
                ) : (
                  <ListItem key={rec.id} sx={itemSx} disablePadding>
                    {itemContent}
                  </ListItem>
                );
              })}
            </List>

            {onAddToOffer && (
              <Button
                variant="contained"
                size="small"
                onClick={() => { void handleAddToOffer(); }}
                disabled={selectedOfferIds.length === 0 || isAddingToOffer}
                sx={{ mb: 2 }}
              >
                {t('offer.addToOffer')}
              </Button>
            )}

            <Divider sx={{ my: 1.5 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="subtitle2">{t('summary.total')}</Typography>
              <Typography variant="subtitle1" fontWeight={600} color="primary.main">
                {formatPrice(inspection.totalRecommendedCents)}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Mechanic */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('summary.mechanic')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <UserAvatar
              firstName={inspection.mechanic.firstName}
              lastName={inspection.mechanic.lastName}
            />
            <Box>
              <Typography variant="body1" fontWeight={500}>
                {inspection.mechanic.firstName} {inspection.mechanic.lastName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {inspection.mechanic.phone}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Link to Work Order Dialog */}
      {canLink && workOrderNumber && (
        <LinkToWorkOrderDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          recommendation={selectedRecommendation}
          workOrderNumber={workOrderNumber}
          existingItems={workOrderItems}
          onLinkToExisting={onLinkRecommendation}
          onCreateAndLink={onCreateAndLinkRecommendation}
          onUnlink={onUnlinkRecommendation}
          isLoading={isLinking}
        />
      )}
    </Box>
  );
}
